import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../home/components/Navigation';
import { Footer } from '../../home/components/Footer';
import { ServiceModal } from '../../home/components/ServiceModal';
import { IndustriesModal } from '../../home/components/IndustriesModal';
import { HowItWorksModal } from '../../home/components/HowItWorksModal';
import { CalendarModal } from '../../home/components/CalendarModal';
import { SEO } from '../../../components/SEO';

export default function AIForLandscapers() {
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [isIndustriesModalOpen, setIsIndustriesModalOpen] = useState(false);
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const handleOpenService = (title: string) => {
    setSelectedService(title);
    setIsServiceModalOpen(true);
  };

  const faqs = [
    {
      question: 'How does AI help a landscaping company manage leads?',
      answer: 'Landscaping is highly seasonal and competitive — homeowners often contact multiple companies and hire whoever responds first. Our AI answers every call and web inquiry immediately, books consultations automatically, and follows up with leads that haven\'t responded, keeping your schedule consistently full.'
    },
    {
      question: 'Can the AI handle requests for different service types like mowing, design, and hardscaping?',
      answer: 'Yes. Your AI is trained on your specific service menu. Whether someone is calling about weekly lawn maintenance, a one-time spring cleanup, landscape design, irrigation, or a full hardscape project, it routes and responds appropriately and books the right type of appointment.'
    },
    {
      question: 'Can the AI handle recurring service scheduling?',
      answer: 'Absolutely. For recurring customers — like weekly mow clients — the AI can answer questions about their schedule, handle rescheduling requests, and communicate service updates. It reduces the administrative burden of managing a large recurring client base significantly.'
    },
    {
      question: 'Will it work during my busy spring and fall seasons?',
      answer: 'Peak season is exactly when you need AI the most. While your crews are in the field and your phone is blowing up, your AI handles unlimited simultaneous calls and never gets overwhelmed. Every lead during your busiest time of year gets a fast, professional response.'
    },
    {
      question: 'I already have an office person who handles calls. Do I still need this?',
      answer: 'AI works alongside your team — it covers after-hours calls, overflow during peak volume, weekends, and any time your office person is unavailable. It also handles the repetitive FAQ-style questions so your team can focus on higher-value tasks like closing bigger jobs.'
    },
    {
      question: 'How quickly can I get started?',
      answer: 'Setup takes 24–48 hours. We onboard your service menu, service areas, pricing structure (if you want to share it), and your scheduling availability. Once live, your AI is answering calls and booking jobs from day one.'
    },
  ];

  return (
    <>
      <SEO
        title="AI for Landscapers | 24/7 AI Receptionist & Scheduling for Landscaping Companies"
        description="Keep your landscaping schedule full year-round. Our AI answers every call, books consultations, and handles recurring customer inquiries automatically — even during your busiest season."
        canonical="/ai-for-landscapers"
        schemas={[
          {
            type: 'Service',
            data: {
              '@type': 'Service',
              name: 'AI Receptionist for Landscapers',
              description: 'AI-powered call answering, lead capture, and job scheduling for landscaping and lawn care companies.',
              provider: { '@type': 'Organization', name: 'Dino Nuggets' },
              areaServed: 'United States',
            },
          },
          {
            type: 'FAQPage',
            data: {
              '@type': 'FAQPage',
              mainEntity: faqs.map(f => ({
                '@type': 'Question',
                name: f.question,
                acceptedAnswer: { '@type': 'Answer', text: f.answer },
              })),
            },
          },
        ]}
      />
      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero */}
        <section className="relative pt-32 pb-20 px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0 opacity-40">
            <img
              src="/readdy-assets/landscapers-hero-001.jpg"
              alt="AI for Landscapers"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-white/70 to-white/95"></div>

          <div className="relative max-w-4xl mx-auto text-center">
            <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
              Built for Landscaping Companies
            </span>
            <h1 className="font-display text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              AI for Landscapers:<br />Keep Your Schedule Full, Year-Round
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto">
              Your crew is out in the field and you can't stop to answer every call. Our AI receptionist handles incoming calls, books consultations, manages recurring customer questions, and keeps your job pipeline full — automatically.
            </p>
            <button
              onClick={() => setIsCalendarOpen(true)}
              className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Get a Free Demo
            </button>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">

            {/* The Problem */}
            <div className="mb-16">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                The Landscaping Lead Problem Most Owners Don't Talk About
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Landscaping is a relationship business, but it starts with a phone call. Homeowners who want their property looking great are actively searching for a reliable company — and they're calling multiple businesses at once. The one who responds first, professionally, almost always wins the job. Our <Link to="/services/ai-lead-follow-up" className="text-primary hover:underline font-medium">automated lead follow-up</Link> makes sure you're always that first response.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                The challenge: you and your team are outside, in the field, equipment running, every single day. Answering the phone isn't always possible — and voicemail is often a dead end. By the time you return the call hours later, the homeowner has already booked your competitor. The same problem plagues <Link to="/ai-for-contractors" className="text-primary hover:underline font-medium">contractors</Link> and other field service businesses.
              </p>
              <p className="text-gray-700 leading-relaxed">
                AI for landscapers solves this. Every call gets answered instantly with a professional response. Consultations get booked automatically. Recurring customers get their questions handled without interrupting your workflow. And you get a full summary of every interaction.
              </p>
            </div>

            {/* What We Do */}
            <div className="mb-16">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                What AI Does for Your Landscaping Business
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Our platform pairs <Link to="/ai-phone-answering-service" className="text-primary hover:underline font-medium">AI phone answering</Link> with <Link to="/ai-appointment-scheduling" className="text-primary hover:underline font-medium">automated scheduling</Link> — trained on your services, your territory, and your seasonal availability.
              </p>

              <div className="space-y-6">
                {[
                  {
                    icon: 'ri-phone-line',
                    title: 'Answer Every Call, Even During Peak Season',
                    body: 'Spring is your busiest time — and the worst time to miss calls. Your AI answers immediately, every time, with a warm and professional greeting using your company name. It handles questions about services, timelines, availability, and pricing, then books the consultation directly to your calendar.'
                  },
                  {
                    icon: 'ri-calendar-check-line',
                    title: 'Book Landscape Consultations Without Back-and-Forth',
                    body: 'New customer wants a quote for full lawn care service or a new landscape design? Your AI books a consultation based on your real-time availability, confirms the appointment automatically via text or email, and sends you a detailed lead summary before you show up.'
                  },
                  {
                    icon: 'ri-repeat-line',
                    title: 'Handle Recurring Customer Questions at Scale',
                    body: 'Clients with weekly mowing, seasonal cleanups, or maintenance contracts call frequently with questions — can we reschedule, is the crew still coming this week, what\'s included in my service? Your AI handles all of this automatically, freeing your team from constant admin calls.'
                  },
                  {
                    icon: 'ri-user-follow-line',
                    title: 'Follow Up on Estimates Automatically',
                    body: 'Sent a quote and never heard back? Your AI sends polite, timely follow-up messages to leads who haven\'t booked, increasing your estimate conversion rate without you having to chase anyone down manually.'
                  },
                ].map(item => (
                  <div key={item.title} className="bg-gray-50 p-6 rounded-lg flex gap-5">
                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg">
                      <i className={`${item.icon} text-2xl text-primary`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-xl text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{item.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="mb-16">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                Why Landscaping Businesses Love Automation
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { stat: '60%', label: 'of landscaping leads never get a return call — meaning 60% of your potential revenue may be walking out the door.' },
                  { stat: '1 hr', label: 'Leads who receive a response within one hour are 7x more likely to book than those who wait longer.' },
                  { stat: '40%', label: 'Typical increase in booked consultations after implementing AI call answering and lead follow-up.' },
                ].map(item => (
                  <div key={item.stat} className="border border-gray-200 rounded-lg p-6 text-center">
                    <div className="text-4xl font-bold text-primary mb-3">{item.stat}</div>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Related Services */}
            <div className="mb-16 bg-gradient-to-br from-gray-50 to-primary/5 p-8 rounded-xl">
              <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">
                Full AI Suite for Landscaping Companies
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { to: '/services/ai-receptionist', icon: 'ri-customer-service-2-line', title: 'AI Receptionist', desc: 'Complete virtual front desk solution across calls, texts, and web chat.' },
                  { to: '/ai-phone-answering-service', icon: 'ri-phone-line', title: 'Phone Answering Service', desc: 'AI that answers in your company name and handles every inquiry professionally.' },
                  { to: '/ai-appointment-scheduling', icon: 'ri-calendar-2-line', title: 'Appointment Scheduling', desc: 'Automatic consultation and estimate booking synced to your calendar.' },
                  { to: '/services/ai-chatbot', icon: 'ri-chat-smile-3-line', title: 'Website Chatbot', desc: 'Capture website visitors as leads 24/7 with intelligent automated chat.' },
                ].map(item => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="bg-white p-5 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group flex gap-4"
                  >
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-2xl text-primary`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Other Industries */}
            <div className="mb-16">
              <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">
                We Also Help Other Field Service Businesses
              </h2>
              <div className="flex flex-wrap gap-3">
                <Link to="/ai-for-contractors" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for Contractors</Link>
                <Link to="/ai-for-roofers" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for Roofers</Link>
                <Link to="/ai-for-rv-parks" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for RV Parks &amp; Campgrounds</Link>
              </div>
            </div>

            {/* FAQ */}
            <div className="mb-16">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-8">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      <i className={`ri-arrow-down-s-line text-2xl text-gray-400 flex-shrink-0 transition-transform ${openFaqIndex === index ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openFaqIndex === index && (
                      <div className="px-6 pb-4 pt-2">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-br from-primary to-secondary text-white p-12 rounded-2xl text-center">
              <h2 className="font-display text-3xl font-bold mb-4">Ready to Fill Your Landscaping Schedule?</h2>
              <p className="text-lg mb-8 text-white/90 max-w-2xl mx-auto">
                Book a free 15-minute demo and we'll show you exactly how AI fits your landscaping business. No commitment, no technical headaches.
              </p>
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Schedule My Free Demo
              </button>
            </div>

          </div>
        </section>

        <Footer
          onOpenService={handleOpenService}
          onOpenIndustries={() => setIsIndustriesModalOpen(true)}
          onOpenHowItWorks={() => setIsHowItWorksModalOpen(true)}
        />
        <ServiceModal isOpen={isServiceModalOpen} onClose={() => setIsServiceModalOpen(false)} title={selectedService} />
        <IndustriesModal isOpen={isIndustriesModalOpen} onClose={() => setIsIndustriesModalOpen(false)} />
        <HowItWorksModal isOpen={isHowItWorksModalOpen} onClose={() => setIsHowItWorksModalOpen(false)} />
        <CalendarModal isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} />
      </div>
    </>
  );
}
